import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Ban, BriefcaseBusiness, CheckCircle2, FileText, Mail, ShieldCheck } from "lucide-react";

const navItems = [
  ["หน้าหลัก", "/"],
  ["KHORN Web App", "/product"],
  ["KHORN LINE", "/line"],
  ["KHORN MCP", "/mcp"],
  ["ราคา", "/#pricing"],
  ["ความปลอดภัย", "/privacy"],
  ["ติดต่อ", "/contact"],
];

const AppNav = () => (
  <header className="sticky top-0 z-50 w-full border-b border-[#E3E6DF] bg-[#FAFAF7]/90 backdrop-blur-xl">
    <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 md:px-8">
      <Link to="/" className="flex items-center gap-3" aria-label="KHORN AI home">
        <span className="relative block h-11 w-[172px] shrink-0 overflow-hidden">
          <img src="/logo_and_name.png" alt="KHORN AI" className="absolute -left-[28px] -top-[60px] h-[168px] max-w-none" />
        </span>
      </Link>

      <nav className="hidden items-center gap-8 lg:flex">
        {navItems.map(([label, href]) => (
          <a key={label} href={href} className="text-xs font-bold uppercase tracking-[0.16em] text-[#6A7168] transition hover:text-[#15130F]">
            {label}
          </a>
        ))}
      </nav>

      <a href="https://app.khorn-ai.com/plan" className="rounded-full bg-[#15130F] px-5 py-3 text-xs font-bold uppercase tracking-[0.16em] text-white transition hover:bg-[#1F2A22]">
        เริ่มใช้งาน
      </a>
    </div>
  </header>
);

const highlights = [
  ["ใช้งานตามเงื่อนไข", "ผู้ใช้ต้องใช้งาน KHORN AI เพื่อวัตถุประสงค์ที่ชอบด้วยกฎหมายและตามเงื่อนไขของบริการเท่านั้น", ShieldCheck],
  ["ผู้ใช้รับผิดชอบข้อมูล", "ผู้ใช้รับผิดชอบข้อมูลที่นำเข้าสู่ระบบ คำสั่งที่ส่งให้ AI และการตรวจสอบผลลัพธ์ก่อนใช้งานจริง", BriefcaseBusiness],
  ["ควบคุมการให้บริการ", "KHORN AI ขอสงวนสิทธิ์ในการจำกัด ระงับ หรือยุติการใช้งานที่ผิดเงื่อนไขหรือมีความเสี่ยงต่อระบบ", Ban],
];

const sections = [
  {
    title: "1. ขอบเขตของนโยบาย",
    body: [
      "นโยบายการให้บริการฉบับนี้ใช้กับเว็บไซต์ แอปพลิเคชัน ระบบ AI สำหรับสำนักงานกฎหมาย ระบบจัดการงานลูกความ ระบบค้นกฎหมาย ระบบร่างเอกสาร และบริการอื่นที่เกี่ยวข้องของ บริษัท ค้อนเอไอ โซลูชั่น จำกัด (\"KHORN AI\", \"เรา\", \"ของเรา\")",
      "เมื่อผู้ใช้สมัครบัญชี ชำระค่าบริการ เข้าถึง หรือใช้งานบริการของ KHORN AI ให้ถือว่าผู้ใช้รับทราบและตกลงปฏิบัติตามนโยบายฉบับนี้ รวมถึงเอกสาร เงื่อนไข หรือนโยบายอื่นที่อ้างอิงบนเว็บไซต์หรือในระบบของเรา",
    ],
  },
  {
    title: "2. การสมัครและสิทธิ์การใช้งาน",
    body: [
      "ผู้ใช้ต้องให้ข้อมูลที่ถูกต้อง เป็นปัจจุบัน และไม่ทำให้เข้าใจผิดในการสมัครหรือใช้งานบริการ หากมีการเปลี่ยนแปลงข้อมูล ผู้ใช้ควรอัปเดตข้อมูลให้ถูกต้องโดยเร็ว",
      "สิทธิ์การใช้งานขึ้นอยู่กับแผนบริการที่เลือก เงื่อนไขของแพ็กเกจ จำนวนผู้ใช้ จำนวนเครดิต ความสามารถของระบบ ระยะเวลาการใช้งาน และข้อจำกัดที่แสดงในหน้าราคา หรือแจ้งในระบบ",
      "บัญชีผู้ใช้ สิทธิ์การเข้าถึง และข้อมูลเข้าสู่ระบบเป็นความรับผิดชอบของผู้ใช้ ผู้ใช้ต้องรักษารหัสผ่านและสิทธิ์ของทีมอย่างระมัดระวัง และต้องแจ้งเราโดยเร็วหากพบการใช้งานผิดปกติ",
    ],
  },
  {
    title: "3. การใช้งานที่ได้รับอนุญาต",
    body: [
      "ผู้ใช้สามารถใช้บริการเพื่อช่วยรับข้อมูลลูกความ จัดเคส จัดหลักฐาน ค้นข้อมูลกฎหมาย ร่างเอกสาร วิเคราะห์เอกสาร มอบหมายงาน ติดตามงาน และบริหารขั้นตอนงานภายในสำนักงานกฎหมายหรือองค์กรของผู้ใช้",
      "ผู้ใช้ต้องมีสิทธิหรือฐานทางกฎหมายที่เหมาะสมในการนำข้อมูล เอกสาร ข้อมูลลูกความ ข้อมูลคู่กรณี หรือข้อมูลบุคคลภายนอกเข้าสู่ระบบ KHORN AI",
      "ผลลัพธ์จาก AI เป็นเครื่องมือช่วยทำงาน ผู้ใช้ต้องตรวจสอบความถูกต้อง ความครบถ้วน ความเหมาะสมทางกฎหมาย และความเหมาะสมเชิงวิชาชีพก่อนนำไปใช้หรือส่งต่อให้บุคคลภายนอก",
    ],
  },
  {
    title: "4. การใช้งานต้องห้าม",
    body: [
      "ห้ามใช้บริการเพื่อกระทำการที่ผิดกฎหมาย ละเมิดสิทธิของผู้อื่น ละเมิดความลับทางวิชาชีพ ละเมิดข้อมูลส่วนบุคคล หรือนำข้อมูลเข้าสู่ระบบโดยไม่มีสิทธิ",
      "ห้ามถอดรหัสหรือย้อนกระบวนการระบบ ห้ามดึงข้อมูลจำนวนมาก ห้ามรบกวนหรือโจมตีระบบ ห้ามทดสอบหรือหลบเลี่ยงมาตรการความปลอดภัย ห้ามใช้งานอัตโนมัติในลักษณะที่เป็นอันตราย หรือพยายามเข้าถึงระบบ ข้อมูล หรือบัญชีที่ไม่ได้รับอนุญาต",
      "ห้ามนำบริการไปใช้เพื่อสร้างคำแนะนำที่หลอกลวง บิดเบือนข้อเท็จจริง ใช้แทนการตัดสินใจของผู้ประกอบวิชาชีพโดยไม่มีการตรวจสอบ หรือใช้ในลักษณะที่ทำให้บุคคลอื่นเข้าใจผิดว่า AI เป็นผู้ให้คำปรึกษาทางกฎหมายที่รับผิดชอบแทนมนุษย์",
      "ห้ามขายต่อ ให้เช่า โอนสิทธิ์ แชร์บัญชี หรือเปิดให้บุคคลภายนอกใช้บริการนอกเหนือจากเงื่อนไขของแผนบริการ เว้นแต่ได้รับอนุญาตเป็นลายลักษณ์อักษรจาก KHORN AI",
    ],
  },
  {
    title: "5. การชำระเงิน แผนบริการ และการยกเลิก",
    body: [
      "ผู้ใช้ต้องชำระค่าบริการตามราคา รอบบิล และเงื่อนไขของแผนที่เลือก การเริ่มใช้งานฟีเจอร์แบบชำระเงินอาจขึ้นอยู่กับการชำระเงินสำเร็จหรือการยืนยันจากผู้ให้บริการชำระเงิน",
      "ผู้ใช้สามารถยกเลิกการต่ออายุแผนบริการได้ตามช่องทางที่ระบบกำหนด การยกเลิกจะมีผลต่อการต่ออายุในรอบถัดไป และผู้ใช้อาจยังคงใช้งานได้จนถึงวันสิ้นสุดรอบบิลปัจจุบันตามเงื่อนไขของระบบ",
      "หากผู้ใช้เปลี่ยนแผนบริการ อัปเกรด ดาวน์เกรด เพิ่มผู้ใช้ หรือเพิ่มความสามารถบางส่วน เงื่อนไข ราคา และผลของการเปลี่ยนแปลงจะเป็นไปตามที่แสดงในระบบหรือที่ตกลงเป็นลายลักษณ์อักษร",
    ],
  },
  {
    title: "6. เครดิต ข้อจำกัด และความพร้อมใช้งาน",
    body: [
      "ฟีเจอร์บางประเภทอาจมีข้อจำกัดด้านเครดิต ปริมาณการใช้งาน จำนวนไฟล์ ขนาดไฟล์ จำนวนผู้ใช้ ความเร็ว หรือข้อจำกัดเชิงเทคนิคอื่น เพื่อรักษาคุณภาพ ความปลอดภัย และความเสถียรของบริการ",
      "เราอาจปรับปรุง เปลี่ยนแปลง จำกัด ระงับ หรือยกเลิกฟีเจอร์บางส่วนได้ตามความจำเป็น เช่น เพื่อแก้ไขข้อผิดพลาด ปรับปรุงความปลอดภัย ปฏิบัติตามกฎหมาย หรือรักษาความเสถียรของระบบ",
      "บริการอาจหยุดชั่วคราวจากการบำรุงรักษา เหตุขัดข้องของระบบ cloud, network, AI provider, payment provider หรือเหตุอื่นที่อยู่นอกเหนือการควบคุมโดยสมเหตุสมผลของ KHORN AI",
    ],
  },
  {
    title: "7. ข้อมูล เอกสาร และความรับผิดชอบของผู้ใช้",
    body: [
      "ผู้ใช้ยังคงเป็นเจ้าของหรือเป็นผู้มีสิทธิในข้อมูลและเอกสารที่นำเข้าสู่ระบบ ทั้งนี้ ผู้ใช้ให้สิทธิ KHORN AI ประมวลผลข้อมูลดังกล่าวเท่าที่จำเป็นเพื่อให้บริการตามคำสั่งของผู้ใช้",
      "ผู้ใช้ต้องไม่อัปโหลดข้อมูลที่ผิดกฎหมาย ละเมิดสิทธิ ละเมิดความลับ หรือข้อมูลที่ไม่มีสิทธิใช้กับบริการ หากการใช้งานของผู้ใช้ก่อให้เกิดข้อเรียกร้อง ความเสียหาย หรือการร้องเรียนจากบุคคลภายนอก ผู้ใช้ต้องรับผิดชอบตามกฎหมายที่เกี่ยวข้อง",
      "ผู้ใช้ควรสำรองข้อมูลสำคัญของตนเองเป็นระยะ KHORN AI ไม่รับประกันว่าบริการจะเป็นระบบจัดเก็บเอกสารถาวรหรือระบบ archive หลักของผู้ใช้ เว้นแต่มีการตกลงเป็นลายลักษณ์อักษรแยกต่างหาก",
    ],
  },
  {
    title: "8. ลักษณะของผลลัพธ์จาก AI",
    body: [
      "KHORN AI เป็นเครื่องมือช่วยทำงาน ไม่ใช่ผู้ประกอบวิชาชีพทนายความ ไม่ใช่สำนักงานกฎหมาย และไม่รับหน้าที่ให้คำปรึกษาทางกฎหมายแทนผู้ใช้หรือผู้ประกอบวิชาชีพ",
      "ผลลัพธ์จาก AI อาจคลาดเคลื่อน ไม่ครบถ้วน ล้าสมัย หรือไม่เหมาะสมกับข้อเท็จจริงเฉพาะกรณี ผู้ใช้ต้องตรวจสอบผลลัพธ์ แหล่งอ้างอิง เอกสาร และข้อกฎหมายก่อนนำไปใช้",
      "การตัดสินใจทางกฎหมาย การส่งเอกสาร การยื่นคำร้อง การทำสัญญา การให้คำแนะนำแก่ลูกความ หรือการดำเนินการใด ๆ ต่อบุคคลภายนอกเป็นความรับผิดชอบของผู้ใช้และผู้ประกอบวิชาชีพที่เกี่ยวข้อง",
    ],
  },
  {
    title: "9. การระงับหรือยุติบริการ",
    body: [
      "KHORN AI ขอสงวนสิทธิ์ในการจำกัด ระงับ หรือยุติการเข้าถึงบัญชีหรือบริการ หากพบหรือมีเหตุอันควรเชื่อว่าผู้ใช้ละเมิดนโยบายนี้ ละเมิดกฎหมาย ใช้งานผิดปกติ มีความเสี่ยงต่อความปลอดภัย หรือก่อให้เกิดความเสียหายต่อระบบ ผู้ใช้ หรือบุคคลภายนอก",
      "ในกรณีที่บัญชีถูกระงับหรือยุติ ผู้ใช้อาจไม่สามารถเข้าถึงฟีเจอร์ ข้อมูล หรือผลลัพธ์บางส่วนได้ เราอาจเก็บข้อมูลบางรายการเท่าที่จำเป็นเพื่อปฏิบัติตามกฎหมาย การตรวจสอบข้อพิพาท หรือการรักษาความปลอดภัย",
    ],
  },
  {
    title: "10. การเปลี่ยนแปลงนโยบาย",
    body: [
      "เราอาจปรับปรุงนโยบายการให้บริการนี้เป็นครั้งคราวเพื่อให้สอดคล้องกับบริการ เทคโนโลยี กฎหมาย หรือแนวปฏิบัติที่เปลี่ยนแปลง โดยจะแสดงวันที่มีผลบังคับใช้ของฉบับล่าสุดไว้บนหน้านี้",
      "การใช้งานบริการต่อไปหลังจากมีการปรับปรุงนโยบาย ให้ถือว่าผู้ใช้รับทราบและตกลงตามนโยบายฉบับที่ปรับปรุงแล้ว",
    ],
  },
  {
    title: "11. ช่องทางติดต่อ",
    body: [
      "หากมีคำถามเกี่ยวกับนโยบายการให้บริการ แผนบริการ บัญชีผู้ใช้ หรือการใช้งานระบบ สามารถติดต่อ บริษัท ค้อนเอไอ โซลูชั่น จำกัด ได้ที่ contact@khorn-ai.com หรือ 081-811-8772",
      "ที่อยู่: 54 ซอย 37 ถนนเฉลิมพระเกียรติร.9 หนองบอน ประเวศ กรุงเทพฯ 10250",
    ],
  },
];

export default function BusinessPolicyPage() {
  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#15130F] selection:bg-[#15130F] selection:text-white">
      <Helmet>
        <title>Terms of Service / Business Policy | KHORN AI</title>
        <meta
          name="description"
          content="Terms of Service และ Business Policy ของ KHORN AI สำหรับบัญชีผู้ใช้ แผนบริการ การใช้งาน AI สำนักงานกฎหมาย ข้อจำกัดบริการ และความรับผิดชอบของผู้ใช้"
        />
        <meta name="keywords" content="KHORN AI terms of service, KHORN AI business policy, นโยบายการให้บริการ, terms of service, AI สำนักงานกฎหมาย, KHORN AI" />
        <meta property="og:title" content="Terms of Service / Business Policy | KHORN AI" />
        <meta property="og:description" content="Terms of Service และ Business Policy สำหรับบริการ KHORN AI และการใช้งาน AI สำหรับสำนักงานกฎหมาย" />
        <meta property="og:url" content="https://www.khorn-ai.com/business-policy" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.khorn-ai.com/business-policy" />
      </Helmet>

      <AppNav />

      <main>
        <section className="relative overflow-hidden border-b border-[#E3E6DF]">
          <div className="absolute left-1/2 top-0 h-[520px] w-[860px] -translate-x-1/2 rounded-full bg-[#EEF3EA]/70 blur-3xl" />
          <div className="relative mx-auto max-w-7xl px-5 py-20 md:px-8 lg:py-28">
            <div className="max-w-4xl">
              <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-[#E3E6DF] bg-[#FFFFFF] px-4 py-2 text-xs font-bold text-[#6A7168] shadow-sm">
                <FileText size={14} />
                Terms of Service / Business Policy
              </div>
              <h1 className="text-5xl font-semibold leading-tight tracking-tight md:text-7xl">
                Terms of Service / Business Policy
              </h1>
              <p className="mt-7 max-w-3xl text-lg leading-relaxed text-[#5F665E]">
                เอกสารนี้คือ Terms of Service และนโยบายการให้บริการหลักสำหรับการใช้งาน KHORN AI ในฐานะระบบ AI สำหรับสำนักงานกฎหมาย
                รวมถึงบัญชีผู้ใช้ แผนบริการ ข้อจำกัด ความรับผิดชอบ และการใช้งานที่ต้องห้าม
              </p>
              <div className="mt-8 text-sm font-semibold text-[#15130F]/52">วันที่มีผลบังคับใช้: 12 มิถุนายน 2569</div>
            </div>
          </div>
        </section>

        <section className="border-b border-[#E3E6DF] bg-[#FFFFFF] py-16">
          <div className="mx-auto grid max-w-7xl gap-4 px-5 md:grid-cols-3 md:px-8">
            {highlights.map(([title, desc, Icon]) => (
              <article key={title} className="rounded-[26px] border border-[#E3E6DF] bg-[#FFFFFF] p-7 shadow-sm">
                <Icon size={24} className="mb-8 text-[#6A7168]" />
                <h2 className="text-2xl font-semibold">{title}</h2>
                <p className="mt-4 text-sm leading-relaxed text-[#5F665E]">{desc}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mx-auto grid max-w-7xl gap-10 px-5 py-16 md:px-8 lg:grid-cols-[280px_1fr] lg:py-24">
          <aside className="hidden lg:block">
            <div className="sticky top-28 rounded-[24px] border border-[#E3E6DF] bg-[#FFFFFF] p-5">
              <div className="mb-4 text-xs font-bold uppercase tracking-[0.18em] text-[#6A7168]">สารบัญ</div>
              <nav className="space-y-2">
                {sections.slice(0, 8).map((section) => (
                  <a key={section.title} href={`#${section.title.split(".")[0]}`} className="block rounded-xl px-3 py-2 text-sm font-medium text-[#5F665E] transition hover:bg-[#FAFAF7] hover:text-[#15130F]">
                    {section.title}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          <article className="space-y-5">
            {sections.map((section) => (
              <section key={section.title} id={section.title.split(".")[0]} className="rounded-[26px] border border-[#E3E6DF] bg-[#FFFFFF] p-7 shadow-sm md:p-9">
                <h2 className="text-2xl font-semibold leading-tight md:text-3xl">{section.title}</h2>
                <div className="mt-5 space-y-4">
                  {section.body.map((paragraph) => (
                    <p key={paragraph} className="text-base leading-relaxed text-[#15130F]/64">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </section>
            ))}

            <div className="rounded-[26px] border border-[#E3E6DF] bg-[#15130F] p-7 text-white md:p-9">
              <CheckCircle2 size={26} className="mb-8 text-[#73d99f]" />
              <h2 className="text-3xl font-semibold">ต้องการสอบถามเรื่องการให้บริการ?</h2>
              <p className="mt-4 text-base leading-relaxed text-white/62">
                ติดต่อทีม KHORN AI เพื่อสอบถามเรื่องบัญชีผู้ใช้ แผนบริการ การใช้งานระบบ หรือเงื่อนไขทางธุรกิจของบริการ
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a href="mailto:contact@khorn-ai.com" className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[#FFFFFF] px-6 text-sm font-bold text-[#15130F] transition hover:bg-[#EEF3EA]">
                  <Mail size={16} />
                  contact@khorn-ai.com
                </a>
                <Link to="/contact" className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-white/20 px-6 text-sm font-bold text-white transition hover:bg-[#FFFFFF] hover:text-[#15130F]">
                  ติดต่อเรา
                  <ArrowRight size={15} />
                </Link>
              </div>
            </div>
          </article>
        </section>

        <section className="border-t border-[#E3E6DF] bg-[#FFFFFF] py-12">
          <div className="mx-auto flex max-w-7xl flex-col gap-5 px-5 text-sm text-[#5F665E] md:flex-row md:items-center md:justify-between md:px-8">
            <div className="font-medium">เอกสารกฎหมายของ KHORN AI</div>
            <nav className="flex flex-wrap gap-x-5 gap-y-3 font-bold text-[#15130F]">
              <Link to="/privacy-policy" className="transition hover:text-[#6A7168]">นโยบายความเป็นส่วนตัว</Link>
              <Link to="/business-policy" className="transition hover:text-[#6A7168]">Terms of Service / Business Policy</Link>
              <Link to="/contact" className="transition hover:text-[#6A7168]">ติดต่อ</Link>
            </nav>
          </div>
        </section>
      </main>
    </div>
  );
}
